<script setup lang="ts">
import { useEmployee } from '@/composables/useEmployee';
import { useSafeArea } from '@/composables/useSafeArea';
import type { DuplicateWeekResponse } from '@/types/dayOffValidation';
import { formatDateForApi, getNextWeeks } from '@/utils/date';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import Dialog from 'primevue/dialog';
import { useToast } from 'primevue/usetoast';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  visible: boolean;
  weekRange: { start: Date; end: Date };
  restaurantId: string;
}>();

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'duplicated'): void;
}>();

const { t } = useI18n();
const toast = useToast();
const { duplicateWeek: duplicateWeekApi } = useEmployee();
const { bottomSafeArea } = useSafeArea();

const showDialog = ref(props.visible);
watch(
  () => props.visible,
  (v) => (showDialog.value = v),
);
watch(showDialog, (v) => emit('update:visible', v));

const duplicateWeeks = ref<number[]>([]);
const duplicateLoading = ref(false);
const duplicateError = ref<string | null>(null);

// Use the new utility function instead of manual calculation
const next20Weeks = computed(() => {
  return getNextWeeks(props.weekRange.start, 20);
});

async function handleDuplicateWeek() {
  duplicateLoading.value = true;
  duplicateError.value = null;
  try {
    const target_week_starts = duplicateWeeks.value
      .map((idx) => {
        const week = next20Weeks.value.find((w) => w.index === idx);
        return week ? formatDateForApi(week.start) : null;
      })
      .filter(Boolean) as string[];

    const { data, error } = await duplicateWeekApi({
      restaurant_id: props.restaurantId,
      source_week_start: formatDateForApi(props.weekRange.start),
      target_week_starts,
    });

    if (error) throw new Error(error as string);

    const response = data as DuplicateWeekResponse;

    if (response.successful.length > 0) {
      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('employee.planning.duplicateWeek.success', { count: response.successful.length }),
        life: 5000,
      });
    }

    if (response.failed.length > 0) {
      // Group failed by reason for better messaging
      const dayOffFailed = response.failed.filter((f) => f.reason === 'day_off');

      if (dayOffFailed.length > 0) {
        toast.add({
          severity: 'warn',
          summary: t('employee.planning.duplicateWeek.partialSuccess'),
          detail: t('employee.planning.duplicateWeek.dayOffConflicts', { count: dayOffFailed.length }),
          life: 8000,
        });
      }
    }

    showDialog.value = false;
    duplicateWeeks.value = [];
    emit('duplicated');
  } catch (e: unknown) {
    if (e instanceof Error) {
      duplicateError.value = e.message;
    } else {
      duplicateError.value = t('employee.planning.duplicateWeek.errorDuplicating');
    }

    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: duplicateError.value,
      life: 5000,
    });
  } finally {
    duplicateLoading.value = false;
  }
}
</script>

<template>
  <Dialog v-model:visible="showDialog" modal :header="t('employee.planning.duplicateWeek.label')" :style="{ width: '30rem', maxWidth: '90vw' }">
    <div class="mb-4 rounded bg-yellow-100 p-2 text-yellow-700">
      <i class="pi pi-exclamation-triangle mr-2"></i>
      {{ t('employee.planning.duplicateWeek.warningOverride') }}
    </div>
    <div class="mb-4 flex flex-col gap-2">
      <p>{{ t('employee.planning.duplicateWeek.selectWeekToDuplicate') }}</p>
      <div class="flex max-h-60 flex-col gap-2 overflow-y-auto rounded-md border p-2">
        <div v-for="week in next20Weeks" :key="week.index" class="py flex h-12 items-center gap-2">
          <Checkbox v-model="duplicateWeeks" :input-id="'week-' + week.index" :value="week.index" />
          <label :for="'week-' + week.index">
            {{ week.label }}
          </label>
        </div>
      </div>
    </div>
    <div v-if="duplicateError" class="mb-2 text-red-600">{{ duplicateError }}</div>
    <div class="flex justify-end gap-2" :style="{ paddingBottom: `calc(${bottomSafeArea} + 12px)` }">
      <Button :label="t('common.cancel')" class="p-button-text" :disabled="duplicateLoading" @click="showDialog = false" />
      <Button
        :label="t('employee.planning.duplicateWeek.duplicate')"
        icon="pi pi-check"
        :loading="duplicateLoading"
        :disabled="duplicateWeeks.length === 0"
        @click="handleDuplicateWeek"
      />
    </div>
  </Dialog>
</template>
